import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Play, TrendingUp, Home, ArrowLeft } from 'lucide-react';
import FlashCardGame from './FlashCardGame';
import MemoryGame from './MemoryGame';
import QuizGame from './QuizGame';
import ProgressTracker from './ProgressTracker';
import { CAPY_CATEGORIES, CAPY_DATA, AGE_GROUPS } from '../../data/capyData';

const CapyCorner = ({ onNavigateHome }) => {
    const [view, setView] = useState('age-menu'); // age-menu, category-menu, game, tracker
    const [selectedAgeGroup, setSelectedAgeGroup] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [progress, setProgress] = useState(
        JSON.parse(localStorage.getItem('capyProgress')) || {}
    );

    const selectAgeGroup = (ageGroup) => {
        setSelectedAgeGroup(ageGroup);
        setView('category-menu');
    };

    const startCategory = (category) => {
        setSelectedCategory(category);
        setView('game');
    };

    const goBackToAgeMenu = () => {
        setSelectedAgeGroup(null);
        setView('age-menu');
    };

    const updateProgress = (wordId, status) => {
        // status: true (known), false (needs practice)
        const newProgress = { ...progress };
        if (!newProgress[wordId]) {
            newProgress[wordId] = { attempts: 0, successes: 0, lastStatus: false };
        }

        newProgress[wordId].attempts += 1;
        newProgress[wordId].lastStatus = status;
        if (status) {
            newProgress[wordId].successes += 1;
        }

        setProgress(newProgress);
        localStorage.setItem('capyProgress', JSON.stringify(newProgress));
    };

    const filteredCategories = selectedAgeGroup
        ? CAPY_CATEGORIES.filter(c => c.ageGroup === selectedAgeGroup.id)
        : [];

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#FFF5F7] via-[#F0FCFF] to-[#F1FBE9] font-nunito flex flex-col items-center py-8 px-4 pb-24 md:pb-8">

            {/* Header / Nav */}
            <div className="w-full max-w-4xl flex justify-between items-center mb-8">
                {view === 'age-menu' ? (
                    <button
                        onClick={onNavigateHome}
                        className="flex items-center text-slate-500 hover:text-slate-800 transition-colors font-bold bg-white/50 px-4 py-2 rounded-full shadow-sm"
                    >
                        <Home size={20} className="mr-2" />
                        Volver a Crecer
                    </button>
                ) : view === 'tracker' ? (
                    <button
                        onClick={() => setView('age-menu')}
                        className="flex items-center text-slate-500 hover:text-slate-800 transition-colors font-bold bg-white/50 px-4 py-2 rounded-full shadow-sm"
                    >
                        <ArrowLeft size={20} className="mr-2" />
                        Volver
                    </button>
                ) : (
                    <div></div> // Empty div for spacing
                )}

                {(view === 'age-menu' || view === 'category-menu') && (
                    <button
                        onClick={() => setView('tracker')}
                        className="flex items-center text-[#8B5E66] bg-[#FFD1DC]/40 hover:bg-[#FFD1DC]/60 px-4 py-2 rounded-full transition-colors font-bold shadow-sm"
                    >
                        <TrendingUp size={20} className="mr-2" />
                        Mi Progreso
                    </button>
                )}
            </div>

            {/* Content */}
            <div className="w-full max-w-4xl flex-grow flex flex-col items-center">

                {/* Age Selection Menu */}
                {view === 'age-menu' && (
                    <div className="w-full text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mb-12"
                        >
                            <h1 className="text-4xl md:text-5xl font-black text-slate-700 mb-4 tracking-tight">
                                El Rincón de <span className="text-[#0073E4]">Capy</span>
                            </h1>
                            <p className="text-xl text-slate-500 max-w-lg mx-auto leading-relaxed">
                                ¡Selecciona tu aventura!
                            </p>
                        </motion.div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full px-4 max-w-2xl mx-auto">
                            {AGE_GROUPS.map(group => (
                                <motion.div
                                    key={group.id}
                                    whileHover={{ scale: 1.05, rotate: 2 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => selectAgeGroup(group)}
                                    className={`${group.color} p-8 rounded-[2.5rem] shadow-lg cursor-pointer flex flex-col items-center text-center transition-shadow hover:shadow-2xl justify-center relative overflow-hidden group border-4 border-white`}
                                >
                                    {group.imagePath ? (
                                        <div className="bg-white/40 p-2 rounded-[2rem] mb-4 shadow-sm backdrop-blur-sm z-10 w-32 h-32 flex items-center justify-center">
                                            <img
                                                src={group.imagePath}
                                                alt={group.name}
                                                className="w-full h-full object-contain drop-shadow-lg"
                                                onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                        </div>
                                    ) : (
                                        <div className="bg-white/40 p-6 rounded-full mb-4 shadow-sm backdrop-blur-sm z-10">
                                            {React.cloneElement(group.icon, { size: 60, className: group.textColor })}
                                        </div>
                                    )}
                                    <h3 className={`text-3xl font-black ${group.textColor} mb-2 z-10`}>
                                        {group.name}
                                    </h3>
                                    <span className={`inline-block px-4 py-1 rounded-full bg-white/60 font-bold ${group.textColor} mb-3 text-sm z-10 shadow-sm`}>
                                        {group.range}
                                    </span>
                                    <p className={`text-base ${group.textColor} font-semibold opacity-90 z-10 px-4`}>
                                        {group.description}
                                    </p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Category Selection Menu */}
                {view === 'category-menu' && selectedAgeGroup && (
                    <div className="w-full text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mb-8 relative flex items-center justify-center"
                        >
                            <button
                                onClick={goBackToAgeMenu}
                                className="absolute left-0 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800 transition-colors bg-white/50 p-3 rounded-full shadow-sm"
                            >
                                <ArrowLeft size={24} />
                            </button>
                            <div className="text-center">
                                <h1 className="text-4xl md:text-5xl font-black text-slate-700 tracking-tight">
                                    {selectedAgeGroup.name}
                                </h1>
                                <p className="text-lg text-slate-500 mt-2 font-semibold">
                                    Elige a qué jugar hoy
                                </p>
                            </div>
                        </motion.div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 w-full px-4 mt-12">
                            {filteredCategories.map(category => (
                                <motion.div
                                    key={category.id}
                                    whileHover={{ scale: 1.03, rotate: 1 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={() => startCategory(category)}
                                    className={`${category.color} p-6 rounded-[2rem] shadow-md cursor-pointer flex flex-col items-center text-center transition-shadow hover:shadow-xl aspect-square justify-center relative overflow-hidden group`}
                                >
                                    {/* Decorative Background Icon */}
                                    <div className="absolute -right-4 -bottom-4 opacity-20 transform rotate-12 group-hover:scale-110 transition-transform duration-500 pointer-events-none">
                                        {React.cloneElement(category.icon, { size: 120 })}
                                    </div>

                                    {/* Categoría Imagen o Icono Central */}
                                    {category.imagePath ? (
                                        <div className="bg-white/30 p-2 rounded-[1.5rem] mb-4 shadow-sm backdrop-blur-sm z-10 pointer-events-none w-24 h-24 flex items-center justify-center">
                                            <img
                                                src={category.imagePath}
                                                alt={category.name}
                                                className="w-full h-full object-contain drop-shadow-md"
                                                onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                        </div>
                                    ) : (
                                        <div className="bg-white/30 p-4 rounded-full mb-4 shadow-sm backdrop-blur-sm z-10 pointer-events-none">
                                            {React.cloneElement(category.icon, { size: 40, className: category.textColor })}
                                        </div>
                                    )}

                                    <h3 className={`text-2xl font-black ${category.textColor} mb-2 z-10 pointer-events-none`}>
                                        {category.name}
                                    </h3>
                                    <p className={`text-sm ${category.textColor} font-semibold opacity-90 z-10 pointer-events-none`}>
                                        {category.description}
                                    </p>

                                    {/* Indicator for specific games (optional detail) */}
                                    {category.gameType !== 'flashcard' && (
                                        <span className={`absolute top-4 right-4 text-xs font-bold uppercase py-1 px-2 rounded-full bg-white/50 ${category.textColor}`}>
                                            {category.gameType === 'memory' ? 'Memoria' : 'Quiz'}
                                        </span>
                                    )}
                                </motion.div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Game Views */}
                {view === 'game' && selectedCategory && (
                    <>
                        {selectedCategory.gameType === 'flashcard' && (
                            <FlashCardGame
                                category={selectedCategory}
                                words={CAPY_DATA.filter(word => word.categoryId === selectedCategory.id)}
                                onUpdateProgress={updateProgress}
                                onFinish={() => setView('category-menu')}
                                onBack={() => setView('category-menu')}
                            />
                        )}
                        {selectedCategory.gameType === 'memory' && (
                            <MemoryGame
                                category={selectedCategory}
                                words={CAPY_DATA.filter(word => word.categoryId === selectedCategory.id)}
                                onUpdateProgress={updateProgress}
                                onFinish={() => setView('category-menu')}
                                onBack={() => setView('category-menu')}
                            />
                        )}
                        {selectedCategory.gameType === 'quiz' && (
                            <QuizGame
                                category={selectedCategory}
                                words={CAPY_DATA.filter(word => word.categoryId === selectedCategory.id)}
                                onUpdateProgress={updateProgress}
                                onFinish={() => setView('category-menu')}
                                onBack={() => setView('category-menu')}
                            />
                        )}
                    </>
                )}

                {/* Tracker View */}
                {view === 'tracker' && (
                    <ProgressTracker
                        progressData={progress}
                        onBack={() => setView('age-menu')}
                    />
                )}

            </div>

        </div>
    );
};

export default CapyCorner;
